import React, { useState, useRef, useEffect } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import '../../styles/report/VirtualBook.css';
import { GiCircleForest, GiPlantRoots, GiPlantSeed, GiPlantWatering, GiPlantsAndAnimals, GiChestnutLeaf } from "react-icons/gi";
// ========== ADDED IMPORTS - MODIFICATION 1 (UPDATED) ==========
import plantDataService from '../../services/plantDataService'; // CHANGED: from gardenService
import gardenService from '../../services/gardenService';
import { fetchWikipediaImage } from '../../utils/wikiImageFetcher';
import EarthLoader from '../EarthLoader';
import ecoReportCacheService from '../../services/ecoReportCacheService';
import placeholderImg from '../../assets/pixel-art/seeding.png';
// =====================================================



// --- Page Components with forwardRef for react-pageflip ---

const PageCover = React.forwardRef(({ title, subtitle }, ref) => (
    <div className="page page-cover" ref={ref} data-density="hard">
        <div className="page-cover-inner">
            {/* <div className="page-cover-left" /> */}
            <div className="page-cover-right">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span className="book-title">{title}</span>
                    {subtitle && (
                        <span style={{
                            marginTop: '15px',
                            fontFamily: 'Cormorant Garamond',
                            fontSize: '18px',
                            fontStyle: 'italic',
                            color: '#6b8c42'
                        }}>
                            {subtitle}
                        </span>
                    )}
                </div>
            </div>
        </div>
    </div>
));

PageCover.displayName = 'PageCover';

const Page = React.forwardRef(({ number, title, children }, ref) => (
    <div className="page" ref={ref}>
        <div className="page-content">
            <h2 className="page-header">{title || `Chapter ${number}`}</h2>

            <div className="page-text scrollable-page-text">{children}</div>
            <div className="page-footer"><span>— {number} —</span></div>
        </div>
    </div>
));

Page.displayName = 'Page';

// --- Main Component ---

export default function VirtualBook() {
    const [page, setPage] = useState(0);
    const [totalPage, setTotalPage] = useState(0);
    const flipBookRef = useRef(null);

    // ========== ADDED STATE - MODIFICATION 2 ==========
    const [plants, setPlants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [milestones, setMilestones] = useState([]);
    const [wildlifeSpotlight, setWildlifeSpotlight] = useState([]);
    const [environmentalTips, setEnvironmentalTips] = useState([]);
    const [reportData, setReportData] = useState(null);
    // ==================================================
    const API_BASE_URL = 'https://your-garden-guru-backend.vercel.app/api';
    // const API_BASE_URL = 'http://localhost:5000/api';


    // ========== ADDED HELPER FUNCTIONS - MODIFICATION 3 ==========
    // const calculateMilestones = (plantData) => {
    //     if (!plantData || plantData.length === 0) {
    //         return [];
    //     }

    //     const totalCarbon = plantData.reduce((total, plant) => {
    //         const carbonImpact = plant.ecoData?.carbon_kg_per_year_stage || 0;
    //         return total + parseFloat(carbonImpact);
    //     }, 0);

    //     const uniquePollinatorValues = new Set();
    //     plantData.forEach(plant => {
    //         if (plant.ecoData?.pollinator_value && plant.ecoData.pollinator_value !== "None") {
    //             uniquePollinatorValues.add(plant.ecoData.pollinator_value);
    //         }
    //     });
    //     const pollinatorCount = uniquePollinatorValues.size;

    //     const shelterCount = plantData.filter(plant =>
    //         plant.ecoData?.provides_shelter === true
    //     ).length;

    //     return [
    //         {
    //             id: 1,
    //             title: "Carbon Milestone",
    //             description: `Your garden has absorbed approximately ${totalCarbon.toFixed(2)} kg of CO₂!`,
    //             icon: "🌿",
    //             achieved: totalCarbon > 0,
    //             date: new Date().toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' }),
    //             color: "rgb(73, 108, 28)",
    //             visualImpact: {
    //                 value: totalCarbon.toFixed(2),
    //                 unit: "kg CO₂",
    //                 comparisons: [
    //                     { icon: "🚗", text: `${(totalCarbon / 0.2236).toFixed(1)} km(s) car trip offset` },
    //                     { icon: "💡", text: `${Math.round(totalCarbon * 6.67)} hours of LED light bulb use` }
    //                 ]
    //             }
    //         },
    //         {
    //             id: 2,
    //             title: "Pollinator Paradise",
    //             description: `Your garden attracts ${pollinatorCount} different pollinator type(s)!`,
    //             icon: "🐝",
    //             achieved: pollinatorCount > 0,
    //             date: new Date().toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' }),
    //             color: "rgb(255, 193, 7)",
    //             visualImpact: {
    //                 value: pollinatorCount,
    //                 unit: "pollinator type(s)",
    //                 comparisons: [
    //                     { icon: "🌸", text: "Bees, butterflies, and other beneficial insects" },
    //                     { icon: "🌻", text: `Biodiversity boost: +${pollinatorCount > 2 ? Math.round((pollinatorCount - 2) / 2 * 100) : 0}%` }
    //                 ]
    //             }
    //         },
    //         {
    //             id: 3,
    //             title: "Wildlife Shelter Champion",
    //             description: `${shelterCount} plants in your garden provide shelter for wildlife!`,
    //             icon: "🏡",
    //             achieved: shelterCount > 0,
    //             date: new Date().toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' }),
    //             color: "rgb(46, 125, 50)",
    //             visualImpact: {
    //                 value: shelterCount,
    //                 unit: "shelter plant(s)",
    //                 comparisons: [
    //                     { icon: "🏙️", text: `Equivalent to ${shelterCount * 4}m² of natural habitat` },
    //                     { icon: "🦋", text: shelterCount >= 3 ? "Supports full butterfly lifecycle" : "Partial butterfly habitat" }
    //                 ]
    //             }
    //         }
    //     ];
    // };
    const calculateMilestones = (plantData) => {
        if (!plantData || plantData.length === 0) {
            return [];
        }

        const totalCarbon = plantData.reduce((total, plant) => {
            const carbonImpact = plant.ecoData?.carbon_kg_per_year_stage || 0;
            return total + parseFloat(carbonImpact);
        }, 0);

        const uniquePollinatorValues = new Set();
        plantData.forEach(plant => {
            if (plant.ecoData?.pollinator_value && plant.ecoData.pollinator_value !== "None") {
                uniquePollinatorValues.add(plant.ecoData.pollinator_value);
            }
        });
        const pollinatorCount = uniquePollinatorValues.size;

        const shelterCount = plantData.filter(plant =>
            plant.ecoData?.provides_shelter === true
        ).length;

        const allMilestones = [
            {
                id: 1,
                title: "Carbon Milestone",
                description: `Your garden has absorbed approximately ${totalCarbon.toFixed(2)} kg of CO₂!`,
                icon: "🌿",
                achieved: totalCarbon > 0,
                date: new Date().toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' }),
                color: "rgb(73, 108, 28)",
                visualImpact: {
                    value: totalCarbon.toFixed(2),
                    unit: "kg CO₂",
                    comparisons: [
                        { icon: "🚗", text: `${(totalCarbon / 0.2236).toFixed(1)} km(s) car trip offset` },
                        { icon: "💡", text: `${Math.round(totalCarbon * 6.67)} hours of LED light bulb use` }
                    ]
                }
            },
            {
                id: 2,
                title: "Pollinator Paradise",
                description: `Your garden attracts ${pollinatorCount} different pollinator type(s)!`,
                icon: "🐝",
                achieved: pollinatorCount > 0,
                date: new Date().toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' }),
                color: "rgb(255, 193, 7)",
                visualImpact: {
                    value: pollinatorCount,
                    unit: "pollinator type(s)",
                    comparisons: [
                        { icon: "🌸", text: "Bees, butterflies, and other beneficial insects" },
                        { icon: "🌻", text: `Biodiversity boost: +${pollinatorCount > 2 ? Math.round((pollinatorCount - 2) / 2 * 100) : 0}%` }
                    ]
                }
            },
            {
                id: 3,
                title: "Wildlife Shelter Champion",
                description: `${shelterCount} plants in your garden provide shelter for wildlife!`,
                icon: "🏡",
                achieved: shelterCount > 0,
                date: new Date().toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' }),
                color: "rgb(46, 125, 50)",
                visualImpact: {
                    value: shelterCount,
                    unit: "shelter plant(s)",
                    comparisons: [
                        { icon: "🏙️", text: `Equivalent to ${shelterCount * 4}m² of natural habitat` },
                        { icon: "🦋", text: shelterCount >= 3 ? "Supports full butterfly lifecycle" : "Partial butterfly habitat" }
                    ]
                }
            }
        ];

        // Filter to only return milestones where visualImpact.value > 0
        return allMilestones.filter(milestone => parseFloat(milestone.visualImpact.value) > 0);
    };

    const calculateTotalCarbon = () => {
        return plants.reduce((total, plant) => {
            const carbonImpact = plant.ecoData?.carbon_kg_per_year_stage || 0;
            return total + parseFloat(carbonImpact);
        }, 0).toFixed(2);
    };

    // Fetch wildlife spotlight data with Wikipedia images
    const fetchWildlifeSpotlight = async (plantsData) => {
        try {
            // First check cache
            const cachedWildlife = ecoReportCacheService.getWildlifeSpotlight(plantsData);
            if (cachedWildlife) {
                console.log("Using cached wildlife spotlight data");
                return cachedWildlife;
            }

            console.log("Fetching new wildlife spotlight data");
            const response = await fetch(`${API_BASE_URL}/gemini/wildlife-spotlight`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ plants: plantsData }),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch wildlife spotlight data');
            }

            const data = await response.json();

            // Fetch Wikipedia images for each wildlife item
            const wildlifeWithImages = await Promise.all(
                data.wildlife.map(async (wildlife) => {
                    // Try to fetch image using scientific name for more precise results
                    const imageData = await fetchWikipediaImage(wildlife.scientificName || wildlife.title);

                    return {
                        ...wildlife,
                        // Use the fetched image or fall back to placeholder
                        image: imageData ? imageData.url : placeholderImg,
                        // Add attribution information
                        imageAttribution: imageData ? imageData.attribution : null,
                        // Add Wikipedia page URL for clickable image
                        wikiPageUrl: imageData ? imageData.wikiPageUrl : null,
                        // Store original placeholder in case wikipedia fetch fails but we need to revert
                        placeholderImage: placeholderImg
                    };
                })
            );

            // Cache the results
            ecoReportCacheService.saveWildlifeSpotlight(plantsData, wildlifeWithImages);

            return wildlifeWithImages;
        } catch (error) {
            console.error('Error fetching wildlife spotlight:', error);
            // Return default data with proper image handling if the API fails
            const defaultWildlife = [
                {
                    title: "Blue-banded Bee",
                    description: "These native bees love your lavender plants! They're crucial pollinators for many Australian plants.",
                    icon: "🐝",
                    scientificName: "Amegilla cingulata",
                    behaviors: "Uses buzz pollination by vibrating their bodies",
                    encouragementTip: "Plant more purple flowering plants and herbs"
                },
                {
                    title: "Eastern Spinebill",
                    description: "This honeyeater bird visits gardens with diverse native flowers and helps with pollination.",
                    icon: "🐦",
                    scientificName: "Acanthorhynchus tenuirostris",
                    behaviors: "Hovers like a hummingbird when feeding",
                    encouragementTip: "Add tubular-shaped native flowers to your garden"
                },
                {
                    title: "Superb Fairy-wren",
                    description: "These small birds with vibrant blue plumage are attracted to gardens with dense shrubs and ground cover.",
                    icon: "🐦",
                    scientificName: "Malurus cyaneus",
                    behaviors: "Highly social birds that hop and flit through undergrowth",
                    encouragementTip: "Plant native shrubs and maintain some leaf litter for foraging"
                }
            ];

            // Try to fetch Wikipedia images for default wildlife
            return await Promise.all(
                defaultWildlife.map(async (wildlife) => {
                    const imageData = await fetchWikipediaImage(wildlife.scientificName);

                    return {
                        ...wildlife,
                        image: imageData ? imageData.url : placeholderImg,
                        imageAttribution: imageData ? imageData.attribution : null,
                        wikiPageUrl: imageData ? imageData.wikiPageUrl : null,
                        placeholderImage: placeholderImg
                    };
                })
            );
        }
    };

    // Fetch environmental tips from Gemini API
    const fetchEnvironmentalTips = async (plantsData) => {
        try {
            // First check cache
            const cachedTips = ecoReportCacheService.getEnvironmentalTips(plantsData);
            if (cachedTips) {
                console.log("Using cached environmental tips");
                return cachedTips;
            }

            console.log("Fetching new environmental tips");
            const response = await fetch(`${API_BASE_URL}/gemini/environmental-tips`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ plants: plantsData }),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch environmental tips');
            }

            const data = await response.json();

            // Cache the tips
            const tips = data.tips.slice(0, 5);
            ecoReportCacheService.saveEnvironmentalTips(plantsData, tips);

            // Return the tips array, with at most 5 tips
            return tips;
        } catch (error) {
            console.error('Error fetching environmental tips:', error);
            // Return default tips if the API fails
            return [
                {
                    id: 1,
                    tip: "Add more flowering plants that support pollinators",
                    reason: "Your garden could benefit from more pollinator-friendly plants",
                    implementation: "Consider adding lavender, grevillea, or bottlebrush",
                    ecologicalBenefit: "Increases biodiversity and supports local bee populations"
                },
                {
                    id: 2,
                    tip: "Group plants with similar watering needs together",
                    reason: "Water efficiency is important in Australian gardens",
                    implementation: "Create hydrozones in your garden based on water requirements",
                    ecologicalBenefit: "Reduces water waste and creates more resilient plant communities"
                },
                {
                    id: 3,
                    tip: "Add a small water feature or bird bath",
                    reason: "Your garden has plants that attract wildlife but limited water sources",
                    implementation: "Install a simple bird bath or small pond in a shaded area",
                    ecologicalBenefit: "Provides essential habitat for birds, insects, and amphibians"
                },
                {
                    id: 4,
                    tip: "Create a compost system for garden waste",
                    reason: "Recycling garden waste improves soil health",
                    implementation: "Set up a simple compost bin in a corner of your garden",
                    ecologicalBenefit: "Reduces waste and creates nutrient-rich soil amendment"
                }
            ];
        }
    };



    const handleImageError = (spotlight) => (e) => {
        console.warn(`Image failed to load for ${spotlight.title}, using placeholder`);
        e.target.src = placeholderImg;
    };
    // =============================================================

    const loadCachedReportData = () => {
        try {
            // Get plants from the garden service
            const gardenPlants = gardenService.getPlants();

            // If no plants, no need to check for cache
            if (!gardenPlants || gardenPlants.length === 0) {
                return null;
            }

            // Use the isCacheValid function from ecoReportCacheService
            // Make sure the function exists and is callable
            if (typeof ecoReportCacheService.isCacheValid !== 'function') {
                console.warn('isCacheValid function not available in ecoReportCacheService');
                return null;
            }

            // Check if we have a valid cache
            let isCacheValid = false;
            try {
                isCacheValid = ecoReportCacheService.isCacheValid(gardenPlants);
            } catch (error) {
                console.error('Error checking cache validity:', error);
                return null;
            }

            if (!isCacheValid) {
                return null;
            }

            // If we have valid cache for both wildlife and tips, build a complete report object
            const cachedWildlife = ecoReportCacheService.getWildlifeSpotlight(gardenPlants);
            const cachedTips = ecoReportCacheService.getEnvironmentalTips(gardenPlants);

            if (cachedWildlife && cachedTips) {
                // Check if we have cached eco report data
                try {
                    const cachedReportData = localStorage.getItem('garden-eco-report-cache');
                    if (!cachedReportData) return null;

                    const reportData = JSON.parse(cachedReportData);

                    // Make sure this cache matches our current plants
                    if (reportData.plantsHash === ecoReportCacheService.calculatePlantsHash(gardenPlants)) {
                        console.log('Using cached eco report data');
                        return {
                            plants: reportData.plants,
                            milestones: reportData.milestones,
                            wildlifeSpotlight: cachedWildlife,
                            environmentalTips: cachedTips
                        };
                    }
                } catch (parseError) {
                    console.error('Error parsing cached report data:', parseError);
                    return null;
                }
            }

            return null;
        } catch (error) {
            console.error('Error loading cached report data:', error);
            return null;
        }
    };

    // ========== ADDED DATA LOADING - MODIFICATION 4 (Updated to use plantDataService) ==========
    const saveReportData = (data) => {
        try {
            if (!data || !data.plants || !Array.isArray(data.plants)) {
                console.warn('Invalid data provided to saveReportData');
                return;
            }

            let plantsHash;
            try {
                plantsHash = ecoReportCacheService.calculatePlantsHash(data.plants);
            } catch (hashError) {
                console.error('Error calculating plants hash:', hashError);
                plantsHash = Date.now().toString(); // Use timestamp as fallback hash
            }

            // Only save essential data to minimize storage size
            const cacheData = {
                plantsHash,
                plants: data.plants,
                milestones: data.milestones,
                timestamp: Date.now()
            };

            localStorage.setItem('garden-eco-report-cache', JSON.stringify(cacheData));
        } catch (error) {
            console.error('Error saving report data:', error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // First check if we have cached report data
                const cachedData = loadCachedReportData();
                if (cachedData) {
                    setPlants(cachedData.plants);
                    setMilestones(cachedData.milestones);
                    setWildlifeSpotlight(cachedData.wildlifeSpotlight);
                    setEnvironmentalTips(cachedData.environmentalTips);
                    setReportData(cachedData);
                    setIsLoading(false);
                    return;
                }

                // Get plants from the garden service
                const gardenPlants = gardenService.getPlants();

                if (!gardenPlants || gardenPlants.length === 0) {
                    // No plants found, just set loading to false and let the UI handle it
                    setIsLoading(false);
                    return; // Exit early to prevent further processing
                }

                // For each plant, fetch its eco data from our NestJS backend
                const plantsWithEcoData = await Promise.all(
                    gardenPlants.map(async (plant) => {
                        try {
                            // Make API call to get eco data for this plant type
                            const response = await fetch(`${API_BASE_URL}/database/plantecodata/${encodeURIComponent(plant.type)}`);

                            if (!response.ok) {
                                throw new Error(`Failed to fetch eco data for ${plant.type}`);
                            }

                            const ecoDataResult = await response.json();

                            // The NestJS endpoint returns data in a specific format
                            // We need to use the first item in the ecoData array if it exists
                            const ecoData = ecoDataResult.ecoData &&
                                ecoDataResult.ecoData.length > 0 ?
                                ecoDataResult.ecoData[0] : null;

                            // Combine plant data with eco data
                            return {
                                ...plant,
                                ecoData: ecoData || {
                                    carbon_kg_per_year_stage: "0",
                                    pollinator_value: "None",
                                    provides_shelter: false
                                }
                            };
                        } catch (err) {
                            console.error(`Error fetching data for ${plant.type}:`, err);

                            // Return plant with default eco data if fetch fails
                            return {
                                ...plant,
                                ecoData: {
                                    carbon_kg_per_year_stage: "0",
                                    pollinator_value: "None",
                                    provides_shelter: false
                                }
                            };
                        }
                    })
                );

                setPlants(plantsWithEcoData);

                // Calculate and set milestones based on the fetched data
                const calculatedMilestones = calculateMilestones(plantsWithEcoData);
                setMilestones(calculatedMilestones);

                // Fetch wildlife spotlight data
                const wildlifeData = await fetchWildlifeSpotlight(plantsWithEcoData);
                setWildlifeSpotlight(wildlifeData);
                console.log("wildlife data: ", wildlifeData);

                // Fetch environmental tips
                const tipsData = await fetchEnvironmentalTips(plantsWithEcoData);
                setEnvironmentalTips(tipsData);
                console.log("tip data: ", tipsData);

                // Build complete report data object
                const completeData = {
                    plants: plantsWithEcoData,
                    milestones: calculatedMilestones,
                    wildlifeSpotlight: wildlifeData,
                    environmentalTips: tipsData
                };

                // Save report data for faster loading next time
                saveReportData(completeData);

                setReportData(completeData);
                setIsLoading(false);
            } catch (err) {
                console.error("Error loading garden eco data:", err);
                setError("Failed to load eco report. Please try again later.");
                setIsLoading(false);
            }
        };

        fetchData();

        // Listen for storage events to update report if garden data changes
        const handleStorageChange = (e) => {
            if (e.key === 'gardenPlants') {
                // Garden plants have changed - invalidate cache and reload
                ecoReportCacheService.invalidateCache();
                localStorage.removeItem('garden-eco-report-cache');
                fetchData();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);


    // ============================================================================================

    useEffect(() => {
        const timer = setTimeout(() => {
            if (flipBookRef.current) {
                try {
                    const pageFlip = flipBookRef.current.pageFlip();
                    if (pageFlip) {
                        setTotalPage(pageFlip.getPageCount());
                    }
                } catch (error) {
                    console.error('Error getting page count:', error);
                }
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [isLoading]); // MODIFIED: Added isLoading dependency

    const nextButtonClick = () => {
        if (flipBookRef.current) {
            flipBookRef.current.pageFlip().flipNext();
        }
    };

    const prevButtonClick = () => {
        if (flipBookRef.current) {
            flipBookRef.current.pageFlip().flipPrev();
        }
    };

    const onPage = (e) => {
        setPage(e.data);
    };

    // ========== ADDED ERROR/LOADING STATES - MODIFICATION 5 ==========
    if (error) {
        return (
            <div className="sunflower-theme-wrapper">
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-brown)' }}>
                    <h2>⚠️ Error Loading Report</h2>
                    <p>{error}</p>
                    <button className="control-btn" onClick={() => window.location.reload()}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="sunflower-theme-wrapper">
                <EarthLoader />
            </div>
        );
    }

    if (plants.length === 0) {
        return (
            <div className="sunflower-theme-wrapper">
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-brown)' }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🌱</div>
                    <h2>No Plants Yet</h2>
                    <p>Start adding plants to your garden to see your environmental impact!</p>
                </div>
            </div>
        );
    }
    // ==================================================================

    // Build pages array dynamically to avoid null children in HTMLFlipBook
    const buildPages = () => {
        const pages = [];

        // Start placeholder
        pages.push(<div key="start-placeholder" className="start-placeholder" />);

        // Cover
        pages.push(
            <PageCover
                key="cover"
                title={<>My Eco<br />Impact</>}
                subtitle="A Garden Journey"
            />
        );

        // Introduction
        pages.push(
            <Page key="intro" number={1} title="Introduction">
                <p>Welcome to your personalised Garden Eco-Report! Discover how your garden is
                    making a positive impact on the environment.</p>
                <p>From carbon absorption to providing habitats for local wildlife, your gardening
                    efforts contribute to a healthier planet.</p>
                <p style={{ marginTop: '25px', fontSize: '20px', fontWeight: 'bold', fontStyle: 'italic' }}>
                    You have {plants.length} plants contributing to environmental health.
                </p>
                <p style={{ fontSize: '20x', fontWeight: 'bold', fontStyle: 'italic' }}>
                    Total carbon absorbed: {calculateTotalCarbon()} kg CO₂ equivalent.
                </p>
            </Page>
        );

        // Milestones
        milestones.forEach((milestone, index) => {
            pages.push(
                <Page key={`milestone-${index}`} number={index + 2} title={milestone.title}>
                    <div style={{ fontSize: '48px', textAlign: 'center', marginBottom: '20px' }}>
                        {milestone.icon}
                    </div>
                    <div style={{
                        textAlign: 'center',
                        fontSize: '36px',
                        fontWeight: '700',
                        color: milestone.color,
                        marginBottom: '10px'
                    }}>
                        {milestone.visualImpact.value}
                    </div>
                    <div style={{
                        textAlign: 'center',
                        fontSize: '18px',
                        color: 'var(--warm-brown)',
                        marginBottom: '20px'
                    }}>
                        {milestone.visualImpact.unit}
                    </div>
                    <p>{milestone.description}</p>
                    <div style={{ marginTop: '20px' }}>
                        {milestone.visualImpact.comparisons.map((comparison, idx) => (
                            <p key={idx} style={{ fontSize: '16px', marginBottom: '8px' }}>
                                {comparison.icon} {comparison.text}
                            </p>
                        ))}
                    </div>
                </Page>
            );
        });

        // Wildlife spotlight (only add if data exists)
        if (wildlifeSpotlight.length > 0) {
            pages.push(
                <Page key="wildlife" number={2 + milestones.length} title="Wildlife Spotlight">
                    {/* <div style={{ fontSize: '48px', textAlign: 'center', marginBottom: '20px' }}>
                        🦋
                    </div> */}
                    <p>Your garden attracts wonderful wildlife! Here are some of the pollinators
                        benefiting from your plants:</p>
                    {wildlifeSpotlight.slice(0, 2).map((spotlight, index) => (
                        <div key={index} style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid rgba(92, 77, 60, 0.2)' }}>
                            <p style={{ fontWeight: '600', fontSize: '20px', marginBottom: '5px' }}>
                                {spotlight.icon} {spotlight.title}
                            </p>
                            <p style={{ fontSize: '14px', fontStyle: 'italic', marginBottom: '8px', opacity: 0.8 }}>
                                {spotlight.scientificName}
                            </p>
                            <p style={{ fontSize: '16px' }}>{spotlight.description}</p>
                            <p style={{ fontSize: '15px', marginTop: '8px' }}>
                                <strong>Tip:</strong> {spotlight.encouragementTip}
                            </p>
                        </div>
                    ))}
                </Page>
            );
        }

        // Environmental tips (first 2-3)
        const wildlifePageOffset = wildlifeSpotlight.length > 0 ? 1 : 0;
        environmentalTips.slice(0, 3).forEach((tip, index) => {
            pages.push(
                <Page key={`tip-${index}`}
                    number={3 + milestones.length + wildlifePageOffset + index}
                    title={`Eco Tip ${tip.id}`}>
                    {/* <div style={{ fontSize: '48px', textAlign: 'center', marginBottom: '20px' }}>
                        🌱
                    </div> */}
                    <h3 style={{
                        fontSize: '22px',
                        color: 'var(--warm-brown)',
                        marginBottom: '15px',
                        fontWeight: '600'
                    }}>
                        {tip.tip}
                    </h3>
                    <p style={{ marginBottom: '15px' }}>{tip.reason}</p>
                    <div style={{
                        padding: '15px',
                        backgroundColor: 'rgba(107, 140, 66, 0.1)',
                        borderRadius: '8px',
                        marginBottom: '15px'
                    }}>
                        <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                            <strong>How to implement:</strong>
                        </p>
                        <p style={{ fontSize: '15px' }}>{tip.implementation}</p>
                    </div>
                    <p style={{ fontSize: '15px', fontStyle: 'italic' }}>
                        <strong>Benefit:</strong> {tip.ecologicalBenefit}
                    </p>
                </Page>
            );
        });

        // End cover
        pages.push(<PageCover key="end-cover" title={<>The<br />End</>} />);

        // End placeholder
        pages.push(<div key="end-placeholder" className="start-placeholder" />);

        return pages;
    };

    return (
        <>
            <div className="sunflower-theme-wrapper">
                <HTMLFlipBook
                    width={600}
                    height={550}
                    size="fixed"
                    maxShadowOpacity={0}
                    showCover={false}
                    mobileScrollSupport={true}
                    onFlip={onPage}
                    className="demo-book"
                    ref={flipBookRef}
                >
                    {buildPages()}
                </HTMLFlipBook>

                <div className="demo-controls">
                    <button
                        className="control-btn"
                        onClick={prevButtonClick}
                        disabled={page === 0}
                    >
                        <ArrowLeft size={16} /> Previous
                    </button>

                    <span className="page-info">
                        Page {page} of {totalPage}
                    </span>

                    <button
                        className="control-btn"
                        onClick={nextButtonClick}
                        disabled={page >= totalPage - 1}
                    >
                        Next <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        </>
    );
}